import React, { useState, useRef, useEffect } from 'react';
import {
  Camera,
  Upload,
  RefreshCw,
  Eye,
  EyeOff,
  AlertCircle,
  Clock,
  Settings,
  Lock,
  Download,
  Copy,
  Check,
  Search,
  Cpu,
  FileText,
  RotateCcw,
  Video,
} from 'lucide-react';
import { AISettings, DetectedFridgeItem, FridgeAnalysisResult } from '../types/vision';
import { analyzeFridgePhoto } from '../services/visionService';

interface ProductionVisionStudioProps {
  onLock: () => void;
}

const AI_STORAGE_KEY = 'fridge_vision_ai_provider_selection_v11';

export const DEFAULT_SYSTEM_PROMPT = `You are an expert AI computer vision system specialized in grocery, food, and refrigerator inventory analysis.
Analyze this photo of a refrigerator, pantry, countertop, or grocery collection.
Identify every visible food, grocery, and beverage item.
For each item provide:
1. "name": specific product name (e.g. "Whole Milk", "Greek Yogurt", "Strawberries", "Eggs Carton", "Cheddar Cheese")
2. "brand": brand name if visible on packaging, otherwise null
3. "category": strictly one of ["Produce", "Dairy & Eggs", "Meat & Seafood", "Pantry & Grains", "Bakery", "Beverages", "Condiments & Sauces", "Leftovers & Prepared", "Frozen", "Snacks", "Other"]
4. "quantity": estimated integer count
5. "unit": appropriate unit (e.g. "Carton (1L)", "pcs", "Tub (500g)", "Jar", "Bottle", "Bag")
6. "confidence": float between 0.80 and 0.99
7. "box": 2D bounding box percentage coordinates on a 0 to 100 scale:
   - "ymin": top edge percentage (0 to 100)
   - "xmin": left edge percentage (0 to 100)
   - "ymax": bottom edge percentage (0 to 100)
   - "xmax": right edge percentage (0 to 100)
8. "freshnessStatus": "fresh", "expiring_soon", or "check_date"
9. "estimatedShelfLifeDays": estimated days it remains fresh
10. "notes": brief location or appearance observation

Return ONLY a valid JSON object matching this schema:
{
  "title": "Short title describing the scene",
  "summary": "2-sentence summary of identified items and overall stock status",
  "eatFirstWarning": "1 sentence on which item should be consumed first to prevent spoilage",
  "suggestedRecipes": ["Recipe Idea 1", "Recipe Idea 2", "Recipe Idea 3"],
  "items": [
    {
      "name": "Whole Milk",
      "brand": "Brand Name",
      "category": "Dairy & Eggs",
      "quantity": 1,
      "unit": "Carton (1L)",
      "confidence": 0.96,
      "box": { "ymin": 15, "xmin": 10, "ymax": 65, "xmax": 30 },
      "freshnessStatus": "fresh",
      "estimatedShelfLifeDays": 7,
      "notes": "Top left"
    }
  ]
}`;

const DEFAULT_AI_SETTINGS: AISettings = {
  provider: 'groq',
  geminiApiKey: '',
  geminiModel: 'gemini-2.5-flash',
  groqApiKey: '',
  groqModel: 'qwen/qwen3.6-27b',
  customPrompt: DEFAULT_SYSTEM_PROMPT,
};

export const ProductionVisionStudio: React.FC<ProductionVisionStudioProps> = ({ onLock }) => {
  const [aiSettings, setAiSettings] = useState<AISettings>(() => {
    try {
      const saved = localStorage.getItem(AI_STORAGE_KEY);
      return saved ? { ...DEFAULT_AI_SETTINGS, ...JSON.parse(saved) } : DEFAULT_AI_SETTINGS;
    } catch {
      return DEFAULT_AI_SETTINGS;
    }
  });

  const [currentResult, setCurrentResult] = useState<FridgeAnalysisResult | null>(null);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [showBoundingBoxes, setShowBoundingBoxes] = useState<boolean>(true);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [settingsActiveTab, setSettingsActiveTab] = useState<'model' | 'prompt'>('model');
  const [cameraFacing, setCameraFacing] = useState<'environment' | 'user'>('environment');
  const [copiedJson, setCopiedJson] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<string>('');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    localStorage.setItem(AI_STORAGE_KEY, JSON.stringify(aiSettings));
  }, [aiSettings]);

  // Video Stream attachment
  useEffect(() => {
    if (isCameraActive && streamRef.current && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = streamRef.current;
      video.onloadedmetadata = () => {
        video.play().catch((err) => console.warn('Video play error:', err));
      };
      video.play().catch((err) => console.warn('Initial play error:', err));
    }
  }, [isCameraActive, cameraFacing]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startLiveCamera = async () => {
    setAnalysisError(null);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: cameraFacing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setIsCameraActive(true);
    } catch (e: any) {
      console.warn('Camera stream error:', e);
      cameraInputRef.current?.click();
    }
  };

  const stopLiveCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const toggleCameraFacing = async () => {
    const next = cameraFacing === 'environment' ? 'user' : 'environment';
    setCameraFacing(next);
    if (isCameraActive) {
      stopLiveCamera();
      setTimeout(async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: next }, width: { ideal: 1920 } },
            audio: false,
          });
          streamRef.current = stream;
          setIsCameraActive(true);
        } catch (e) {
          console.warn('Flip camera error', e);
        }
      }, 150);
    }
  };

  const capturePhotoFromLiveStream = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    stopLiveCamera();
    runInference(dataUrl);
  };

  const handlePhotoCaptured = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      if (dataUrl) runInference(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const runInference = async (dataUrl: string) => {
    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const result = await analyzeFridgePhoto(dataUrl, aiSettings, 'refrigerator');
      setCurrentResult(result);
      setIsAnalyzing(false);
      setSelectedItemId(null);
    } catch (err: any) {
      setIsAnalyzing(false);
      setAnalysisError(err.message || 'Analysis failed. Please check your API settings.');
    }
  };

  const handleExportCSV = () => {
    if (!currentResult) return;
    const rows = [
      ['Item', 'Brand', 'Category', 'Quantity', 'Unit', 'Confidence', 'Freshness', 'Est. Days Left'],
      ...currentResult.detectedItems.map((it) => [
        `"${it.name}"`,
        `"${it.brand || ''}"`,
        `"${it.category}"`,
        it.quantity,
        `"${it.unit}"`,
        `${Math.round(it.confidence * 100)}%`,
        it.freshnessStatus,
        it.estimatedShelfLifeDays,
      ]),
    ];
    const csvContent = 'data:text/csv;charset=utf-8,' + rows.map((e) => e.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `inventory-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyJson = () => {
    if (!currentResult?.rawJsonOutput) return;
    navigator.clipboard.writeText(currentResult.rawJsonOutput);
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 2000);
  };

  const resetPromptToDefault = () => {
    setAiSettings((prev) => ({
      ...prev,
      customPrompt: DEFAULT_SYSTEM_PROMPT,
    }));
  };

  const filteredItems = currentResult
    ? currentResult.detectedItems.filter(
        (it) =>
          it.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
          it.category.toLowerCase().includes(searchFilter.toLowerCase()) ||
          (it.brand || '').toLowerCase().includes(searchFilter.toLowerCase())
      )
    : [];

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#222326] flex flex-col font-sans">
      {/* Hidden File Inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoCaptured}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoCaptured}
        className="hidden"
      />

      {/* Top Navbar */}
      <header className="w-full bg-[#FAF7F2] border-b border-[#E8E2D8] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#C85032]" />
            <h1 className="text-xl font-bold tracking-tight text-[#1A1B1F] font-serif">
              Fridge Vision
            </h1>
          </div>

          <div className="hidden sm:flex items-center space-x-2 text-xs text-[#706E6B] pl-4 border-l border-[#E2DAD0]">
            <span className="font-medium text-[#1A1B1F]">
              {aiSettings.provider === 'groq'
                ? `Groq (${aiSettings.groqModel})`
                : `Gemini (${aiSettings.geminiModel})`}
            </span>
            {currentResult?.latencyMs && (
              <span className="text-[10px] text-[#284439] bg-[#E4EEE9] px-2 py-0.5 rounded-full font-medium">
                {currentResult.latencyMs}ms
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2.5">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="px-3.5 py-2 rounded-xl text-xs font-medium border border-[#DCD3C7] bg-white text-[#2B2C30] hover:bg-[#F5EFE6] transition flex items-center space-x-2 shadow-sm"
          >
            <Settings className="w-3.5 h-3.5 text-[#C85032]" />
            <span>Settings</span>
          </button>

          <button
            onClick={onLock}
            className="p-2 rounded-xl bg-white border border-[#DCD3C7] text-[#706E6B] hover:text-[#1A1B1F] transition shadow-sm"
            title="Lock Session"
          >
            <Lock className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-6xl w-full mx-auto p-4 sm:p-8 flex flex-col justify-center">
        {/* Error Notice */}
        {analysisError && (
          <div className="mb-6 bg-[#FCF0ED] border border-[#F3C5BA] rounded-2xl p-4 text-xs text-[#B3381B] flex items-start space-x-3 shadow-sm">
            <AlertCircle className="w-5 h-5 text-[#C85032] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <span className="font-semibold block text-[#8F260E]">Notice</span>
              <p className="mt-0.5">{analysisError}</p>
            </div>
          </div>
        )}

        {/* State 1: Empty Workspace */}
        {!currentResult && !isCameraActive && !isAnalyzing && (
          <div className="max-w-xl mx-auto w-full py-12 flex flex-col items-center text-center">
            <h2 className="text-3xl sm:text-4xl font-normal text-[#1A1B1F] tracking-tight font-serif">
              Scan your fridge or pantry.
            </h2>

            <p className="text-sm sm:text-base text-[#66635F] mt-3 font-sans leading-relaxed">
              Take a photo to identify ingredients, monitor shelf life, and track what you have in stock.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3 mt-8 w-full max-w-md">
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="w-full sm:flex-1 py-3.5 px-6 bg-[#C85032] hover:bg-[#B34327] text-white font-medium rounded-2xl text-sm flex items-center justify-center space-x-2 transition active:scale-[0.99] shadow-sm"
              >
                <Camera className="w-4 h-4" />
                <span>Take Photo</span>
              </button>

              <button
                onClick={startLiveCamera}
                className="w-full sm:flex-1 py-3.5 px-4 bg-white hover:bg-[#F7F2EA] text-[#2B2C30] font-medium rounded-2xl text-sm flex items-center justify-center space-x-2 transition border border-[#D8CFC3] active:scale-[0.99] shadow-sm"
              >
                <Video className="w-4 h-4 text-[#706E6B]" />
                <span>Live Viewfinder</span>
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full sm:w-auto py-3.5 px-5 bg-white hover:bg-[#F7F2EA] text-[#2B2C30] font-medium rounded-2xl text-sm flex items-center justify-center space-x-2 transition border border-[#D8CFC3] active:scale-[0.99] shadow-sm"
                title="Upload Photo"
              >
                <Upload className="w-4 h-4 text-[#706E6B]" />
                <span className="sm:hidden">Upload Photo</span>
              </button>
            </div>
          </div>
        )}

        {/* State 2: Live Viewfinder */}
        {isCameraActive && (
          <div className="max-w-3xl mx-auto w-full">
            <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden bg-black border border-[#E5DDD2] shadow-2xl">
              <video
                ref={(node) => {
                  videoRef.current = node;
                  if (node && streamRef.current && node.srcObject !== streamRef.current) {
                    node.srcObject = streamRef.current;
                    node.play().catch(() => {});
                  }
                }}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 flex space-x-2">
                <button
                  onClick={toggleCameraFacing}
                  className="p-3 rounded-2xl bg-black/60 backdrop-blur-md text-white hover:bg-black/80 transition"
                  title="Switch camera"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              <div className="absolute bottom-6 left-0 right-0 flex justify-center space-x-4 px-4">
                <button
                  onClick={capturePhotoFromLiveStream}
                  className="bg-[#C85032] hover:bg-[#B34327] text-white font-medium px-7 py-3 rounded-2xl shadow-xl flex items-center space-x-2 text-sm transition active:scale-95 shadow-[#C85032]/30"
                >
                  <Camera className="w-4 h-4" />
                  <span>Capture & Analyze</span>
                </button>
                <button
                  onClick={stopLiveCamera}
                  className="bg-white/95 backdrop-blur-md text-[#2B2C30] font-medium px-5 py-3 rounded-2xl text-sm border border-[#D5CABE] hover:bg-white transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* State 3: Analyzing In Progress */}
        {isAnalyzing && (
          <div className="max-w-md mx-auto w-full py-24 flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 rounded-full border-2 border-[#C85032] border-t-transparent animate-spin mb-4" />
            <h3 className="text-lg font-normal text-[#1A1B1F] font-serif">Analyzing image...</h3>
            <p className="text-xs text-[#706E6B] mt-1 font-sans">
              Processing with {aiSettings.provider === 'groq' ? `Groq (${aiSettings.groqModel})` : 'Google Gemini'}
            </p>
          </div>
        )}

        {/* State 4: Results Workspace */}
        {currentResult && !isCameraActive && !isAnalyzing && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 w-full">
            {/* Left: Image & Precision Bounding Boxes (7 Cols) */}
            <div className="lg:col-span-7 flex flex-col space-y-3.5">
              <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden bg-white border border-[#E5DDD2] shadow-xl flex items-center justify-center">
                <img
                  src={currentResult.imageUrl}
                  alt={currentResult.title}
                  className="w-full h-full object-cover"
                />

                {/* Bounding Boxes */}
                {showBoundingBoxes && (
                  <div className="absolute inset-0 pointer-events-none">
                    {currentResult.detectedItems.map((item) => {
                      const isHovered = hoveredItemId === item.id || selectedItemId === item.id;
                      const box = item.box;
                      const color = item.color || '#C85032';

                      return (
                        <div
                          key={item.id}
                          style={{
                            top: `${box.ymin}%`,
                            left: `${box.xmin}%`,
                            width: `${Math.max(6, box.xmax - box.xmin)}%`,
                            height: `${Math.max(6, box.ymax - box.ymin)}%`,
                            borderColor: color,
                          }}
                          className={`absolute border-2 rounded-lg transition-all duration-100 pointer-events-auto cursor-pointer ${
                            isHovered
                              ? 'bg-white/20 ring-2 ring-white z-20 shadow-lg'
                              : 'bg-black/10 hover:bg-white/10 z-10'
                          }`}
                          onMouseEnter={() => setHoveredItemId(item.id)}
                          onMouseLeave={() => setHoveredItemId(null)}
                          onClick={() => setSelectedItemId(item.id)}
                        >
                          <div
                            style={{ backgroundColor: color }}
                            className="absolute -top-3 left-0 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white whitespace-nowrap shadow leading-tight font-sans"
                          >
                            {item.name} ({Math.round(item.confidence * 100)}%)
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Action Toolbar */}
              <div className="bg-white border border-[#E8E2D8] rounded-2xl p-2.5 flex items-center justify-between shadow-sm">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="bg-[#C85032] hover:bg-[#B34327] text-white px-3.5 py-1.5 rounded-xl text-xs font-medium flex items-center space-x-1.5 transition active:scale-95 shadow-sm"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    <span>New Photo</span>
                  </button>

                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-[#F6F1E8] hover:bg-[#ECE4D8] text-[#2B2C30] px-3.5 py-1.5 rounded-xl text-xs font-medium flex items-center space-x-1.5 transition border border-[#DDD4C7]"
                  >
                    <Upload className="w-3.5 h-3.5 text-[#706E6B]" />
                    <span>Upload</span>
                  </button>

                  <button
                    onClick={() => setShowBoundingBoxes((prev) => !prev)}
                    className="text-xs text-[#706E6B] hover:text-[#1A1B1F] px-2.5 py-1.5 rounded-xl flex items-center space-x-1 transition"
                  >
                    {showBoundingBoxes ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    <span>Boxes</span>
                  </button>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleExportCSV}
                    className="p-1.5 px-2.5 rounded-xl bg-[#F6F1E8] hover:bg-[#ECE4D8] text-[#2B2C30] border border-[#DDD4C7] text-xs flex items-center space-x-1"
                    title="Export CSV"
                  >
                    <Download className="w-3.5 h-3.5 text-[#706E6B]" />
                    <span className="hidden sm:inline">CSV</span>
                  </button>

                  {currentResult.rawJsonOutput && (
                    <button
                      onClick={handleCopyJson}
                      className="p-1.5 px-2.5 rounded-xl bg-[#F6F1E8] hover:bg-[#ECE4D8] text-[#2B2C30] border border-[#DDD4C7] text-xs flex items-center space-x-1"
                      title="Copy JSON"
                    >
                      {copiedJson ? <Check className="w-3.5 h-3.5 text-[#284439]" /> : <Copy className="w-3.5 h-3.5 text-[#706E6B]" />}
                      <span className="hidden sm:inline">JSON</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Detected Items & Freshness (5 Cols) */}
            <div className="lg:col-span-5 flex flex-col space-y-4">
              {/* Freshness Notice */}
              {currentResult.eatFirstWarning && (
                <div className="bg-[#FAF3ED] border border-[#F0DFD1] rounded-2xl p-3.5 flex items-start space-x-3 shadow-sm">
                  <Clock className="w-4 h-4 text-[#C85032] flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <span className="font-semibold text-[#C85032] block">Freshness Notice</span>
                    <p className="text-[#4F443B] mt-0.5 leading-relaxed font-sans">{currentResult.eatFirstWarning}</p>
                  </div>
                </div>
              )}

              {/* Items Card */}
              <div className="bg-white border border-[#E8E2D8] rounded-3xl p-5 flex-1 flex flex-col shadow-sm">
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-[#EFE9DF]">
                  <div>
                    <h3 className="text-base font-normal text-[#1A1B1F] font-serif">Detected Items</h3>
                    <p className="text-[11px] text-[#706E6B] font-sans mt-0.5">
                      {currentResult.detectedItems.length} items cataloged
                    </p>
                  </div>

                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-[#9E9B95] absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Filter..."
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      className="bg-[#FAF8F5] border border-[#E0D7CB] rounded-xl pl-7 pr-3 py-1 text-xs text-[#1A1B1F] placeholder:text-[#9E9B95] focus:outline-none w-28 sm:w-36 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="space-y-2 overflow-y-auto max-h-[380px] pr-1">
                  {filteredItems.length === 0 ? (
                    <div className="p-6 text-center text-xs text-[#8E8B85]">No matching items found.</div>
                  ) : (
                    filteredItems.map((item) => {
                      const isHovered = hoveredItemId === item.id || selectedItemId === item.id;
                      const isExpiring = item.freshnessStatus === 'expiring_soon';
                      const color = item.color || '#C85032';

                      return (
                        <div
                          key={item.id}
                          onMouseEnter={() => setHoveredItemId(item.id)}
                          onMouseLeave={() => setHoveredItemId(null)}
                          onClick={() => setSelectedItemId(item.id)}
                          className={`p-3 rounded-2xl border transition-all text-xs cursor-pointer ${
                            isHovered
                              ? 'bg-[#FAF5EE] border-[#C85032]/40 shadow-sm'
                              : 'bg-[#FAF8F5] border-[#EAE3D8] hover:bg-[#F5EFE6]'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <span className="font-semibold text-[#1A1B1F] text-sm block font-serif">{item.name}</span>
                              <div className="flex items-center space-x-2 text-[11px] text-[#706E6B] mt-0.5 font-sans">
                                {item.brand && <span>{item.brand} •</span>}
                                <span>
                                  {item.quantity} {item.unit}
                                </span>
                                <span>•</span>
                                <span style={{ color }} className="font-medium">{item.category}</span>
                              </div>
                            </div>

                            <div className="text-right font-sans">
                              <span className="text-xs font-semibold text-[#1A1B1F] block">
                                {Math.round(item.confidence * 100)}%
                              </span>
                              <span
                                className={`text-[10px] font-medium block mt-0.5 ${
                                  isExpiring ? 'text-[#C85032]' : 'text-[#284439]'
                                }`}
                              >
                                ~{item.estimatedShelfLifeDays}d shelf life
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Recipe Suggestions */}
              {currentResult.suggestedRecipes && currentResult.suggestedRecipes.length > 0 && (
                <div className="bg-white border border-[#E8E2D8] rounded-3xl p-4 shadow-sm">
                  <h4 className="text-xs font-semibold text-[#1A1B1F] font-serif mb-2">Recipe ideas</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {currentResult.suggestedRecipes.map((recipe, idx) => (
                      <span
                        key={idx}
                        className="bg-[#FAF6F0] border border-[#E6DDCF] text-[#2B2C30] text-xs px-3 py-1 rounded-xl font-medium font-sans"
                      >
                        {recipe}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl bg-white border border-[#E5DDD2] rounded-3xl p-6 sm:p-8 shadow-2xl space-y-5 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-[#EFE9DF] flex-shrink-0">
              <h3 className="text-lg font-normal text-[#1A1B1F] font-serif">Settings</h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-[#706E6B] hover:text-[#1A1B1F] p-1"
              >
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div className="flex space-x-2 border-b border-[#EFE9DF] pb-2 flex-shrink-0">
              <button
                onClick={() => setSettingsActiveTab('model')}
                className={`px-4 py-2 text-xs font-semibold rounded-xl transition flex items-center space-x-1.5 ${
                  settingsActiveTab === 'model'
                    ? 'bg-[#C85032] text-white shadow-sm'
                    : 'bg-[#FAF7F2] text-[#706E6B] hover:text-[#1A1B1F]'
                }`}
              >
                <Cpu className="w-3.5 h-3.5" />
                <span>AI Model</span>
              </button>

              <button
                onClick={() => setSettingsActiveTab('prompt')}
                className={`px-4 py-2 text-xs font-semibold rounded-xl transition flex items-center space-x-1.5 ${
                  settingsActiveTab === 'prompt'
                    ? 'bg-[#C85032] text-white shadow-sm'
                    : 'bg-[#FAF7F2] text-[#706E6B] hover:text-[#1A1B1F]'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span>System Prompt</span>
              </button>
            </div>

            {/* Tab 1: Model Selection */}
            {settingsActiveTab === 'model' && (
              <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                <div>
                  <label className="block text-xs font-medium text-[#706E6B] mb-1.5">Provider</label>
                  <div className="grid grid-cols-2 gap-2 bg-[#FAF7F2] p-1.5 rounded-2xl border border-[#EAE3D8]">
                    <button
                      onClick={() => setAiSettings((prev) => ({ ...prev, provider: 'groq' }))}
                      className={`py-2.5 text-xs font-semibold rounded-xl transition ${
                        aiSettings.provider === 'groq'
                          ? 'bg-[#284439] text-white shadow-sm'
                          : 'text-[#706E6B] hover:text-[#1A1B1F]'
                      }`}
                    >
                      Groq Vision (Default)
                    </button>
                    <button
                      onClick={() => setAiSettings((prev) => ({ ...prev, provider: 'gemini' }))}
                      className={`py-2.5 text-xs font-semibold rounded-xl transition ${
                        aiSettings.provider === 'gemini'
                          ? 'bg-[#C85032] text-white shadow-sm'
                          : 'text-[#706E6B] hover:text-[#1A1B1F]'
                      }`}
                    >
                      Google Gemini
                    </button>
                  </div>
                </div>

                {aiSettings.provider === 'groq' && (
                  <div className="space-y-3 text-xs">
                    <div>
                      <label className="block text-[11px] text-[#706E6B] mb-1 font-medium font-sans">Groq Model</label>
                      <select
                        value={aiSettings.groqModel}
                        onChange={(e) => setAiSettings((prev) => ({ ...prev, groqModel: e.target.value }))}
                        className="w-full bg-[#FAF8F5] border border-[#E0D7CB] rounded-xl px-3.5 py-2.5 text-[#1A1B1F] focus:outline-none focus:bg-white"
                      >
                        <option value="qwen/qwen3.6-27b">
                          qwen/qwen3.6-27b (Official Groq Vision)
                        </option>
                      </select>
                    </div>
                  </div>
                )}

                {aiSettings.provider === 'gemini' && (
                  <div className="space-y-3 text-xs">
                    <div>
                      <label className="block text-[11px] text-[#706E6B] mb-1 font-medium font-sans">Model</label>
                      <select
                        value={aiSettings.geminiModel}
                        onChange={(e) => setAiSettings((prev) => ({ ...prev, geminiModel: e.target.value }))}
                        className="w-full bg-[#FAF8F5] border border-[#E0D7CB] rounded-xl px-3.5 py-2.5 text-[#1A1B1F] focus:outline-none focus:bg-white"
                      >
                        <option value="gemini-2.5-flash">gemini-2.5-flash (Fast & Accurate)</option>
                        <option value="gemini-3-flash-preview">gemini-3-flash-preview (Frontier Speed)</option>
                        <option value="gemini-2.5-pro">gemini-2.5-pro (High Detail)</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="bg-[#FAF7F2] border border-[#EAE3D8] rounded-2xl p-3.5 text-xs text-[#706E6B]">
                  API keys are stored securely server-side on Cloudflare (`GROQ_API_KEY`, `GEMINI_API_KEY`).
                </div>
              </div>
            )}

            {/* Tab 2: Prompt Editor */}
            {settingsActiveTab === 'prompt' && (
              <div className="space-y-3 overflow-y-auto flex-1 pr-1">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-xs font-semibold text-[#1A1B1F] font-serif">
                      System Prompt
                    </label>
                    <p className="text-[11px] text-[#706E6B]">
                      Customize instructions for item detection and JSON format.
                    </p>
                  </div>
                  <button
                    onClick={resetPromptToDefault}
                    className="text-xs text-[#C85032] hover:underline font-medium flex items-center space-x-1"
                    title="Reset to default prompt"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Reset</span>
                  </button>
                </div>

                <div className="relative">
                  <textarea
                    rows={12}
                    value={aiSettings.customPrompt || DEFAULT_SYSTEM_PROMPT}
                    onChange={(e) => setAiSettings((prev) => ({ ...prev, customPrompt: e.target.value }))}
                    className="w-full font-mono text-xs bg-[#FAF8F5] border border-[#E0D7CB] rounded-2xl p-4 text-[#1A1B1F] focus:outline-none focus:bg-white focus:border-[#C85032] leading-relaxed resize-y"
                    placeholder="Enter system prompt..."
                  />
                  <div className="absolute bottom-3 right-3 text-[10px] font-mono text-[#706E6B] bg-white/90 px-2 py-0.5 rounded border border-[#E0D7CB]">
                    {(aiSettings.customPrompt || DEFAULT_SYSTEM_PROMPT).length} chars
                  </div>
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-[#EFE9DF] flex-shrink-0 flex items-center justify-end space-x-2">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="py-2 px-5 bg-[#C85032] hover:bg-[#B34327] text-white font-medium rounded-xl text-xs transition shadow-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
